import { makeAutoObservable } from "mobx";
import en from "./en.json";
import ru from "./ru.json";
import fr from "./fr.json";
import es from "./es.json";

export type SupportedLanguage = "en" | "ru" | "fr" | "es";
type TranslationTree = Record<string, unknown>;

export const LANDING_LANGUAGES: ReadonlyArray<{
  code: SupportedLanguage;
  nativeName: string;
}> = [
  { code: "en", nativeName: "English" },
  { code: "ru", nativeName: "Русский" },
  { code: "fr", nativeName: "Français" },
  { code: "es", nativeName: "Español" },
];

const packs: Record<SupportedLanguage, TranslationTree> = { en, ru, fr, es };

export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === "string" && LANDING_LANGUAGES.some(({ code }) => code === value);
}

export class LandingI18nStore {
  language: SupportedLanguage = "en";
  translations: TranslationTree = { landing: en };

  constructor() {
    makeAutoObservable(this);
  }

  initialize() {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem("aid_language");
    this.setLanguage(isSupportedLanguage(stored) ? stored : "en");
  }

  setLanguage = (language: SupportedLanguage) => {
    this.language = language;
    this.translations = { landing: packs[language] };
    if (typeof document !== "undefined") document.documentElement.lang = language;
    if (typeof window !== "undefined") window.localStorage.setItem("aid_language", language);
  };

  t = (key: string, params?: Record<string, string | number>): string => {
    const value = key.split(".").reduce<unknown>((node, part) => {
      if (!node || typeof node !== "object") return undefined;
      return (node as TranslationTree)[part];
    }, this.translations);
    if (typeof value !== "string") return key;
    return Object.entries(params ?? {}).reduce(
      (text, [name, replacement]) => text.replaceAll(`{${name}}`, String(replacement)),
      value,
    );
  };
}

export const i18nStore = new LandingI18nStore();
